import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, PauseCircle, LockKeyhole, Ban, Pencil, Info, ChevronRight, Gift } from 'lucide-react';
import {
  accountSummary,
  updateSpendLimit,
  pauseAccount,
  suspendAccount,
  closeAccount,
  updateMarketing,
} from '@/lib/api';
import { formatGBP, formatGBPfromPence, formatDateLong } from '@/lib/format';

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const SectionCard = ({ title, kicker, children, testid, action }) => (
  <div className="bg-surface border border-pd rounded-2xl p-5 md:p-6" data-testid={testid}>
    <div className="flex items-start justify-between gap-3">
      <div>
        {kicker ? <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--pd-gold)]">{kicker}</div> : null}
        <h2 className="font-display text-2xl mt-1">{title}</h2>
      </div>
      {action}
    </div>
    <div className="mt-4">{children}</div>
  </div>
);

const SpendLimitEditor = ({ summary, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(summary?.spend_limit?.current_gbp ?? '');
  const [noCap, setNoCap] = useState(summary?.spend_limit?.no_limit_acknowledged || false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const hasNumeric = amount !== '' && amount !== null && !isNaN(Number(amount));
    if (!hasNumeric && !noCap) {
      toast.error('Enter a limit or tick "no limit".');
      return;
    }
    setSaving(true);
    try {
      const res = await updateSpendLimit({
        spend_limit_gbp: hasNumeric ? Number(amount) : null,
        no_spend_limit_acknowledged: !hasNumeric && noCap,
      });
      if (res.applied === 'cooling_off_24h') {
        toast.info(`Increase queued — will apply in 24 hours (at ${formatDateTime(res.effective_at)}).`);
      } else {
        toast.success('New limit applied immediately.');
      }
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update spend limit.');
    } finally { setSaving(false); }
  };

  const sl = summary?.spend_limit || {};
  const currentGbp = sl.current_gbp;
  const pendingGbp = sl.pending_gbp;
  const effectiveAt = sl.effective_at;
  const mtdPence = sl.mtd_pence || 0;
  const nextResetAt = sl.next_reset_at;

  const pct = currentGbp && currentGbp > 0 ? Math.min(100, Math.round((mtdPence / (currentGbp * 100)) * 100)) : 0;

  return (
    <SectionCard
      title="Monthly spend limit"
      kicker="Player protection"
      testid="dashboard-spend-limit"
      action={
        !editing ? (
          <button onClick={() => setEditing(true)} className="text-sm text-[color:var(--pd-accent-yellow)] inline-flex items-center gap-1" data-testid="spend-limit-edit">
            <Pencil size={14} /> Edit
          </button>
        ) : null
      }
    >
      {!editing ? (
        <div>
          <div className="flex flex-wrap items-baseline gap-3">
            <div className="font-display text-4xl text-[color:var(--pd-text)]" data-testid="spend-limit-current">
              {currentGbp === null || currentGbp === undefined ? 'No limit set' : formatGBP(currentGbp)}
            </div>
            <div className="text-sm text-muted-sage">per calendar month</div>
          </div>
          {currentGbp !== null && currentGbp !== undefined ? (
            <div className="mt-4">
              <div className="h-2 rounded-full bg-[color:var(--pd-bg)] border border-pd overflow-hidden">
                <div className="h-full bg-[color:var(--pd-accent-yellow)] transition-all" style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-2 flex flex-wrap justify-between gap-2 text-xs text-muted-sage" data-testid="spend-limit-progress">
                <span>{formatGBPfromPence(mtdPence)} of {formatGBP(currentGbp)} used this month</span>
                <span>Resets {formatDateTime(nextResetAt)}</span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-sage mt-3">You haven&rsquo;t set a personal limit. The platform-wide £250/month cap still applies to your account.</p>
          )}
          {pendingGbp !== null && pendingGbp !== undefined ? (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-[color:var(--pd-gold)]/40 bg-[color:var(--pd-bg)] p-3 text-xs text-muted-sage" data-testid="spend-limit-pending">
              <Info size={14} className="text-[color:var(--pd-gold)] mt-0.5 shrink-0" />
              <div>
                <span className="text-[color:var(--pd-text)]">Increase to {pendingGbp === null ? 'no limit' : formatGBP(pendingGbp)}</span>{' '}
                is queued and will take effect on <strong>{formatDateTime(effectiveAt)}</strong> (24-hour cooling-off period).
                Until then, your current limit continues to apply.
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="space-y-3">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-sage">New limit (£, max 250)</span>
            <input
              type="number"
              min={0}
              max={250}
              step={5}
              value={amount}
              onChange={(e) => { setAmount(e.target.value); if (e.target.value !== '') setNoCap(false); }}
              disabled={noCap}
              className={`mt-1 w-full h-11 rounded-xl bg-[color:var(--pd-bg)] border border-pd px-3 text-sm ${noCap ? 'opacity-50 cursor-not-allowed' : 'focus:border-[color:var(--pd-accent-yellow)]'}`}
              data-testid="spend-limit-input"
            />
          </label>
          <label className="flex items-start gap-2 text-sm text-muted-sage">
            <input type="checkbox" checked={noCap} onChange={(e) => { setNoCap(e.target.checked); if (e.target.checked) setAmount(''); }} className="mt-1 accent-[color:var(--pd-accent-yellow)]" data-testid="spend-limit-nocap" />
            <span>Remove my personal limit (platform £250/month cap still applies). Waits 24 hours before taking effect.</span>
          </label>
          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={saving} className="bg-[color:var(--pd-accent-yellow)] text-[#14251c] rounded-xl px-4 py-2 text-sm font-medium disabled:opacity-60" data-testid="spend-limit-save">
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} className="border border-pd rounded-xl px-4 py-2 text-sm" data-testid="spend-limit-cancel">Cancel</button>
          </div>
          <p className="text-xs text-muted-sage">
            Decreases take effect straight away. Increases (or removing the limit) are held for 24 hours.
          </p>
        </div>
      )}
    </SectionCard>
  );
};

const MarketingCard = ({ summary, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const blocked = Boolean(summary?.blocked_reason);
  const on = summary?.user?.marketing_opt_in;
  const toggle = async () => {
    setSaving(true);
    try {
      await updateMarketing(!on);
      toast.success(on ? 'Marketing emails turned off.' : 'You will receive marketing emails.');
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update preferences.');
    } finally { setSaving(false); }
  };
  return (
    <SectionCard title="Marketing preferences" kicker="Communications" testid="dashboard-marketing">
      <p className="text-sm text-muted-sage">
        Transactional emails (entry confirmations, winner notifications) are always sent — these are essential to running the competition.
        Marketing emails about new competitions are optional.
      </p>
      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={toggle}
          disabled={saving || blocked}
          className={`inline-flex items-center rounded-xl px-4 py-2 text-sm font-medium ${on ? 'bg-[color:var(--pd-accent-yellow)] text-[#14251c]' : 'border border-pd text-[color:var(--pd-text)]'} disabled:opacity-60`}
          data-testid="marketing-toggle"
        >
          {on ? 'Marketing emails: On' : 'Marketing emails: Off'}
        </button>
        {blocked ? <span className="text-xs text-muted-sage">Marketing is off during your pause/suspension.</span> : null}
      </div>
    </SectionCard>
  );
};

const PauseSuspendCard = ({ summary, onSaved }) => {
  const [busy, setBusy] = useState(false);

  const pauseUntil = summary?.pause_until;
  const suspendedUntil = summary?.suspended_until;
  const closed = Boolean(summary?.closed_at);

  const doPause = async (duration) => {
    const label = duration === '24h' ? '24 hours' : '7 days';
    if (!window.confirm(`Pause your account for ${label}? During this time you will not be able to enter competitions.`)) return;
    setBusy(true);
    try { await pauseAccount(duration); toast.success(`Account paused for ${label}.`); onSaved?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not pause.'); }
    finally { setBusy(false); }
  };

  const doSuspend = async () => {
    const step1 = window.prompt(
      'Formal suspension lasts a minimum of 6 months and CANNOT be reversed early by you. During this time you will not be able to enter competitions and you will not receive marketing.\n\nType SUSPEND to confirm.',
    );
    if (step1 !== 'SUSPEND') { if (step1 !== null) toast.info('Suspension cancelled.'); return; }
    setBusy(true);
    try { await suspendAccount(); toast.success('Formal suspension applied.'); onSaved?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not suspend.'); }
    finally { setBusy(false); }
  };

  const doClose = async () => {
    const step1 = window.prompt(
      'Closing your account is permanent. You will not be able to enter competitions and your account will be closed.\n\nType CLOSE to confirm.',
    );
    if (step1 !== 'CLOSE') { if (step1 !== null) toast.info('Closure cancelled.'); return; }
    setBusy(true);
    try { await closeAccount(); toast.success('Account closed.'); onSaved?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not close.'); }
    finally { setBusy(false); }
  };

  return (
    <SectionCard title="Take a break" kicker="Player protection" testid="dashboard-pause">
      {closed ? (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 text-red-200 p-4 text-sm" data-testid="account-closed-banner">
          Your account is closed as of {formatDateTime(summary.closed_at)}.
        </div>
      ) : suspendedUntil ? (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 text-red-200 p-4 text-sm" data-testid="account-suspended-banner">
          Formally suspended until <strong>{formatDateTime(suspendedUntil)}</strong>. This cannot be reversed early.
        </div>
      ) : pauseUntil ? (
        <div className="rounded-xl border border-[color:var(--pd-gold)]/50 bg-[color:var(--pd-bg)] p-4 text-sm text-[color:var(--pd-text)]" data-testid="account-paused-banner">
          Paused until <strong>{formatDateTime(pauseUntil)}</strong>. Your access will resume automatically.
        </div>
      ) : (
        <p className="text-sm text-muted-sage">
          Everyone deserves the option to step away. Choose a short pause any time, or a formal 6-month suspension for a firmer break.
          All choices silence marketing emails during the break. If you&apos;re here because something feels off, our{' '}
          <Link to="/safer-play" className="text-[color:var(--pd-accent-yellow)] underline" data-testid="pause-safer-play-link">Safer Play</Link>{' '}
          page has free, confidential support options too.
        </p>
      )}

      {!closed && !suspendedUntil ? (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button disabled={busy} onClick={() => doPause('24h')} className="inline-flex items-center justify-center gap-2 border border-pd rounded-xl px-4 py-3 text-sm hover:bg-[color:var(--pd-bg)] disabled:opacity-60" data-testid="pause-24h">
            <PauseCircle size={16} /> Pause for 24 hours
          </button>
          <button disabled={busy} onClick={() => doPause('7d')} className="inline-flex items-center justify-center gap-2 border border-pd rounded-xl px-4 py-3 text-sm hover:bg-[color:var(--pd-bg)] disabled:opacity-60" data-testid="pause-7d">
            <PauseCircle size={16} /> Pause for 7 days
          </button>
        </div>
      ) : null}

      {!closed && !suspendedUntil ? (
        <div className="mt-6 pt-6 border-t border-pd space-y-3">
          <button
            disabled={busy}
            onClick={doSuspend}
            className="w-full inline-flex items-center justify-center gap-2 border border-red-500/40 bg-red-500/10 text-red-200 rounded-xl px-4 py-3 text-sm hover:bg-red-500/20 disabled:opacity-60"
            data-testid="suspend-btn"
          >
            <LockKeyhole size={16} /> Formal suspension (6 months, irreversible)
          </button>
          <button
            disabled={busy}
            onClick={doClose}
            className="w-full inline-flex items-center justify-center gap-2 border border-red-500/40 bg-transparent text-red-200 rounded-xl px-4 py-3 text-sm hover:bg-red-500/10 disabled:opacity-60"
            data-testid="close-btn"
          >
            <Ban size={16} /> Close my account permanently
          </button>
        </div>
      ) : null}
    </SectionCard>
  );
};

const GiftsList = ({ grants }) => {
  if (!grants || grants.length === 0) return null;
  const statusLabel = (s) => {
    switch (s) {
      case 'LIVE': return { text: 'In the draw', tone: 'text-[color:var(--pd-accent-yellow)]' };
      case 'COMING_SOON': return { text: 'Opens soon', tone: 'text-[color:var(--pd-gold)]' };
      case 'SELECTING_WINNER': return { text: 'Selecting winner', tone: 'text-[color:var(--pd-accent-orange)]' };
      case 'DRAWN':
      case 'WINNER_ANNOUNCED': return { text: 'Winner drawn', tone: 'text-[color:var(--pd-text)]' };
      default: return { text: (s || '').replace(/_/g, ' ') || '\u2014', tone: 'text-muted-sage' };
    }
  };
  return (
    <div className="space-y-3">
      {grants.map((g) => {
        const st = statusLabel(g.competition_status);
        return (
          <div key={g.id} className="bg-[color:var(--pd-bg)] border border-[color:var(--pd-gold)]/40 rounded-xl p-4" data-testid="account-grant">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase inline-flex items-center gap-1.5">
                  <Gift size={11} /> Gifted &middot; {formatDateLong(g.granted_at)}
                </div>
                <Link to={`/competitions/${g.competition_slug}`} className="font-display text-lg leading-tight inline-block mt-0.5">{g.competition_title}</Link>
                {g.reason ? (
                  <div className="mt-1 text-xs text-muted-sage italic">&ldquo;{g.reason}&rdquo;</div>
                ) : null}
              </div>
              <div className="text-right">
                <div className={`font-mono text-[10px] uppercase ${st.tone}`}>{st.text}</div>
                <div className="font-display text-xl text-[color:var(--pd-gold)]">Free</div>
              </div>
            </div>
            {g.ticket_numbers && g.ticket_numbers.length > 0 ? (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                {g.ticket_numbers.map((tn) => (
                  <div key={tn} className="bg-surface border border-pd rounded-lg px-3 py-2 font-mono text-xs" data-testid="account-grant-ticket">{tn}</div>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const OrdersList = ({ orders, hasGrants }) => {
  if (!orders || orders.length === 0) {
    if (hasGrants) {
      // Grants are rendered above; don't tell a user with gifted tickets
      // that they have "no entries yet".
      return (
        <div className="text-sm text-muted-sage" data-testid="orders-empty">
          No paid entries yet. <Link to="/competitions" className="text-[color:var(--pd-accent-yellow)] inline-flex items-center gap-1">Browse competitions <ChevronRight size={14} /></Link>
        </div>
      );
    }
    return (
      <div className="text-sm text-muted-sage" data-testid="orders-empty">
        No entries yet. <Link to="/competitions" className="text-[color:var(--pd-accent-yellow)] inline-flex items-center gap-1">Browse competitions <ChevronRight size={14} /></Link>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {orders.map((o) => (
        <div key={o.order_ref} className="bg-[color:var(--pd-bg)] border border-pd rounded-xl p-4" data-testid="account-order">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-mono text-[10px] text-muted-sage uppercase">{o.order_ref} · {formatDateLong(o.created_at)}</div>
              <Link to={`/competitions/${o.competition_slug}`} className="font-display text-lg leading-tight inline-block mt-0.5">{o.competition_title}</Link>
            </div>
            <div className="text-right">
              <div className="font-mono text-[10px] text-muted-sage uppercase">{(o.payment_status || 'pending').toUpperCase()}</div>
              <div className="font-display text-xl text-[color:var(--pd-accent-yellow)]">{formatGBPfromPence(o.amount_total)}</div>
            </div>
          </div>
          {o.tickets && o.tickets.length > 0 ? (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              {o.tickets.map((t) => (
                <div key={t.ticket_number} className="bg-surface border border-pd rounded-lg px-3 py-2 font-mono text-xs" data-testid="account-ticket">{t.ticket_number}</div>
              ))}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
};

const AccountPage = () => {
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);

  const load = useCallback(() => {
    if (!localStorage.getItem('pd_user_token')) { nav('/login'); return; }
    accountSummary().then(setSummary).catch(() => {
      localStorage.removeItem('pd_user_token');
      localStorage.removeItem('pd_user');
      nav('/login');
    });
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const signOut = () => {
    localStorage.removeItem('pd_user_token');
    localStorage.removeItem('pd_user');
    nav('/');
  };

  const wins = useMemo(() => summary?.wins || [], [summary]);
  const orders = useMemo(() => summary?.orders || [], [summary]);
  const grants = useMemo(() => summary?.grants || [], [summary]);

  if (!summary) {
    return (
      <div className="container-pd py-20"><div className="font-mono text-sm text-muted-sage">Loading your account…</div></div>
    );
  }

  return (
    <div className="container-pd py-8 md:py-14" data-testid="account-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-[0.2em]">Account</div>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05] md:leading-none mt-2" data-testid="account-name">{summary.user.full_name || 'Your account'}</h1>
          <p className="text-muted-sage mt-1.5 md:mt-2 text-sm">{summary.user.email}</p>
        </div>
        <button onClick={signOut} className="border border-pd rounded-xl px-4 py-2 text-sm" data-testid="sign-out-button">Sign out</button>
      </div>

      {summary.blocked_reason ? (
        <div className="mt-5 md:mt-6 flex items-start gap-3 rounded-2xl border border-[color:var(--pd-gold)]/50 bg-surface p-4" data-testid="account-blocked-banner">
          <AlertTriangle size={20} className="text-[color:var(--pd-gold)] mt-0.5 shrink-0" />
          <div className="text-sm text-[color:var(--pd-text)]">
            {summary.blocked_reason}{' '}
            <span className="text-muted-sage">You cannot enter competitions during this time.</span>
          </div>
        </div>
      ) : null}

      <div className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <SpendLimitEditor summary={summary} onSaved={load} />
        <MarketingCard summary={summary} onSaved={load} />
      </div>

      {/* Platform-wide £250/mo hard cap (moral commitment, stricter than DCMS Code) */}
      {summary.credit_card_cap ? (
        <div className="mt-5">
          <SectionCard title="Monthly spend cap" kicker="Platform-wide · £250 per month" testid="dashboard-monthly-cap">
            <p className="text-sm text-muted-sage">
              Every account on The Padel Draw is capped at <span className="text-[color:var(--pd-text)]">£250 total spend per calendar month</span>, regardless of card type. This is a moral commitment we&rsquo;ve made that is stricter than the DCMS Code &mdash; it means no one can accidentally overspend here. Your personal limit, if lower, applies within this ceiling.
            </p>
            <div className="mt-4">
              <div className="h-2 rounded-full bg-[color:var(--pd-bg)] border border-pd overflow-hidden">
                <div
                  className="h-full bg-[color:var(--pd-gold)] transition-all"
                  style={{ width: `${Math.min(100, Math.round(((summary.credit_card_cap.mtd_credit_pence || 0) / (summary.credit_card_cap.cap_pence || 25000)) * 100))}%` }}
                />
              </div>
              <div className="mt-2 flex flex-wrap justify-between gap-2 text-xs text-muted-sage" data-testid="monthly-cap-progress">
                <span>{formatGBPfromPence(summary.credit_card_cap.mtd_credit_pence || 0)} of £250 used this month</span>
                <span>Resets {formatDateTime(summary.credit_card_cap.next_reset_at)}</span>
              </div>
              {summary.credit_card_cap.at_cap ? (
                <div className="mt-3 rounded-xl border border-[color:var(--pd-gold)]/50 bg-[color:var(--pd-bg)] p-3 text-xs text-[color:var(--pd-text)]">
                  You&rsquo;ve reached your £250 monthly cap. It will reset at the start of next month.
                </div>
              ) : null}
            </div>
          </SectionCard>
        </div>
      ) : null}

      <div className="mt-5">
        <PauseSuspendCard summary={summary} onSaved={load} />
      </div>

      {wins.length > 0 ? (
        <div className="mt-8">
          <SectionCard title="Your wins" kicker="Congratulations" testid="dashboard-wins">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {wins.map((w) => (
                <Link key={w.id} to={`/winners/${w.id}`} className="bg-[color:var(--pd-bg)] border border-pd rounded-xl p-4" data-testid="account-win">
                  <div className="font-mono text-[10px] text-muted-sage uppercase">{formatDateLong(w.drawn_at)}</div>
                  <div className="font-display text-xl mt-1">{w.prize}</div>
                  <div className="text-sm text-[color:var(--pd-gold)] mt-1">Ticket {w.ticket_number}</div>
                </Link>
              ))}
            </div>
          </SectionCard>
        </div>
      ) : null}

      {grants.length > 0 ? (
        <div className="mt-5">
          <SectionCard title="Gifted entries" kicker="From The Padel Draw" testid="dashboard-gifts">
            <GiftsList grants={grants} />
          </SectionCard>
        </div>
      ) : null}

      <div className="mt-5">
        <SectionCard title="Entry history" kicker="Orders" testid="dashboard-orders">
          <OrdersList orders={orders} hasGrants={grants.length > 0} />
        </SectionCard>
      </div>

      <p className="mt-8 md:mt-10 text-xs text-muted-sage">
        Player-protection features are provided under the DCMS Prize Draws &amp; Competitions Code of Practice.
        See our{' '}
        <Link to="/terms" className="text-[color:var(--pd-accent-yellow)]">Terms &amp; Conditions</Link>{' '}and{' '}
        <Link to="/privacy" className="text-[color:var(--pd-accent-yellow)]">Privacy Policy</Link>.
      </p>
    </div>
  );
};

export default AccountPage;
